import { useNavigate } from 'react-router-dom';
import { Avatar } from '@vnetwork/ui/primitives';
import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { formatDate } from '@/lib/format';
import type { EmployeeRecord } from '@/api/employees';

const CONTRACT_LABEL: Record<string, string> = {
  intern: 'Intern',
  probation: 'Probation',
  official: 'Official',
  collaborator: 'Collaborator',
  advisor: 'Advisor',
};

function employeeStatusTone(status: number): 'success' | 'danger' | 'muted' {
  if (status === 1) return 'success';
  if (status === 0) return 'danger';
  return 'muted';
}

function employeeStatusLabel(status: number): string {
  if (status === 1) return 'Active';
  if (status === 0) return 'Inactive';
  return String(status);
}

/** Avatar + name cell — click navigates to the detail page. */
function EmployeeNameCell({ row }: { row: EmployeeRecord }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/employees/${row.employee_id}`)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'var(--vn-primary-text)',
        fontWeight: 'var(--vn-font-weight-medium)',
        fontSize: 'var(--vn-font-size-sm)',
        textAlign: 'left',
      }}
    >
      <Avatar name={row.employee_name} size={28} appearance="subtle" tone="accent" />
      {row.employee_name}
    </button>
  );
}

/** Columns for Active/Resigned employee tabs. */
export const employeeColumns: ColumnDef<EmployeeRecord, unknown>[] = [
  {
    id: 'employee_name',
    header: 'Name',
    accessorKey: 'employee_name',
    cell: ({ row }) => <EmployeeNameCell row={row.original} />,
  },
  {
    id: 'employee_code',
    header: 'Code',
    accessorKey: 'employee_code',
    cell: ({ getValue }) => (getValue() as string) || '—',
  },
  {
    id: 'department_name',
    header: 'Department',
    accessorKey: 'department_name',
  },
  {
    id: 'position_name',
    header: 'Position',
    accessorKey: 'position_name',
  },
  {
    id: 'employee_contract_type',
    header: 'Contract',
    accessorKey: 'employee_contract_type',
    cell: ({ getValue }) => {
      const v = getValue() as string;
      return CONTRACT_LABEL[v] ?? v;
    },
  },
  {
    id: 'employee_seniority_level',
    header: 'Seniority',
    accessorKey: 'employee_seniority_level',
    cell: ({ getValue }) => {
      const v = getValue() as string | null;
      return v ? v.charAt(0).toUpperCase() + v.slice(1) : '—';
    },
  },
  {
    id: 'employee_email_company',
    header: 'Company email',
    accessorKey: 'employee_email_company',
  },
  {
    id: 'employee_phone_number',
    header: 'Phone',
    accessorKey: 'employee_phone_number',
    cell: ({ getValue }) => (getValue() as string) || '—',
  },
  {
    id: 'employee_gender',
    header: 'Gender',
    accessorKey: 'employee_gender',
    cell: ({ getValue }) => {
      const v = getValue() as string;
      return v ? v.charAt(0).toUpperCase() + v.slice(1) : '—';
    },
  },
  {
    id: 'employee_start_date',
    header: 'Start date',
    accessorKey: 'employee_start_date',
    cell: ({ getValue }) => formatDate(getValue() as string),
  },
  {
    id: 'employee_end_date',
    header: 'End date',
    accessorKey: 'employee_end_date',
    cell: ({ getValue }) => formatDate(getValue() as string | null),
  },
  {
    id: 'employee_status',
    header: 'Status',
    accessorKey: 'employee_status',
    cell: ({ getValue }) => {
      const v = getValue() as number;
      return <StatusPill tone={employeeStatusTone(v)}>{employeeStatusLabel(v)}</StatusPill>;
    },
  },
];

/** Columns for the Departments tab (grouped view — one row per department). */
export interface DepartmentRow {
  department_id: number;
  department_name: string;
  department_code: string;
  department_status: string;
  department_leader_name?: string;
  employee_count?: number;
}

export const departmentColumns: ColumnDef<DepartmentRow, unknown>[] = [
  {
    id: 'department_name',
    header: 'Department',
    accessorKey: 'department_name',
  },
  {
    id: 'department_code',
    header: 'Code',
    accessorKey: 'department_code',
    cell: ({ getValue }) => (getValue() as string) || '—',
  },
  {
    id: 'department_leader_name',
    header: 'Leader',
    accessorKey: 'department_leader_name',
    cell: ({ getValue }) => (getValue() as string) || '—',
  },
  {
    id: 'employee_count',
    header: 'Employees',
    accessorKey: 'employee_count',
    cell: ({ getValue }) => (getValue() as number) ?? 0,
  },
  {
    id: 'department_status',
    header: 'Status',
    accessorKey: 'department_status',
    cell: ({ getValue }) => {
      const v = (getValue() as string) ?? '';
      const tone = v === 'active' ? 'success' : 'muted';
      return <StatusPill tone={tone}>{v || '—'}</StatusPill>;
    },
  },
];
